import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  Alert,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { DesignDialog, DesignDialogButton } from "../../../common/dashboard/DesignDialog";
import {
  createInvoiceButtonSx,
  InvoiceSoldToKind,
  PreviewInvoiceButton,
  SoldToToggle,
} from "../../../common/dashboard/InvoiceFormControls";
import { scrollToFirstInvalidField } from "../../../common/dashboard/scrollToFirstInvalidField";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { TableEmptyState } from "../../../common/dashboard/TableEmptyState";
import {
  CmpEventSponsorshipRequestFieldsFragment,
  CmpPublicEventPricingPreviewKindEnum,
  GetCmpStaffEventSponsorshipPricingPreviewQuery,
  useGetCmpStaffEventSponsorshipPricingPreviewLazyQuery,
  CmpEventRegistrationStatusEnum,
  CmpEventSponsorshipRequestStatusEnum,
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDocumentEmailModeEnum,
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
  CmpPaymentChoiceTypeEnum,
  useApproveCmpEventSponsorshipRequestMutation,
  useSaveCmpEventInvoiceDraftMutation,
  CmpOperationalSourceKindEnum,
  useCreateCmpEventSponsorshipRequestMutation,
  useEmailCmpInvoiceDocumentMutation,
  useExportCmpEventSponsorsCsvLazyQuery,
  useGetCmpEventDetailQuery,
  useGetCmpEventRegistrationsQuery,
  useGetCmpEventSponsorshipRequestByOperationLazyQuery,
  useGetCmpEventSponsorshipRequestsQuery,
  useGetCmpInvoiceDetailQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useGetCmpManageEventsQuery,
  useRecordCmpEventSponsorshipManualPaymentMutation,
  useUpdateCmpEventSponsorshipRequestMutation,
} from "../../../../graphql/types";
import {
  getEventInvoiceDraftSavedMessage,
  parseEventInvoiceDraftForm,
  serializeEventInvoiceDraftForm,
  toEventInvoiceDraftLineInputs,
  useEventInvoiceDraftFromRoute,
} from "./eventInvoiceDrafts";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import { scrollDashboardToTop } from "../../../../hooks/useScrollOnTabChange";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  stickyActionColumnCellSx,
  stickyActionColumnHeaderSx,
} from "../../../common/dashboard/DataTable";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import UnsavedChangesPrompt, {
  UnsavedChangesDialog,
} from "../../../common/UnsavedChangesPrompt";
import InvoiceCreatedDialog from "../../Invoicing/InvoiceCreatedDialog";
import { refreshAfterConfirmedSave } from "../../Invoicing/refreshAfterConfirmedSave";
import { useEventSalesReadback } from "./eventSalesReadback";
import { invoicePageLayoutPresentation } from "../../Invoicing/invoicePageHeaderPresentation";
import { InvoiceDesignScope } from "../../Invoicing/InvoiceDesignSystem";
import InvoiceSearchableSelect from "../../Invoicing/InvoiceSearchableSelect";
import { isReceiptEligiblePaymentStatus } from "../../Invoicing/invoiceFormatting";
import { getSavedInvoiceRecipientEmail } from "../../Invoicing/invoiceDeliveryFeedback";
import {
  CMP_INVOICE_CREATE_LOOKUP_LIMIT,
  MEMBER_ONLY_INVOICE_LOOKUP_PARTS,
} from "../../Invoicing/invoiceLookupConfig";
import {
  getStaffInvoiceDraftPaymentMethods,
  InvoiceSuccessDialog,
  PaymentProcessedDialog,
} from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { sortRowsByInvoiceNumber } from "../../Invoicing/ManageInvoices/invoiceNumberSorting";
import ManageInvoiceActionConfirmationDialog from "../../Invoicing/ManageInvoices/ManageInvoiceActionConfirmationDialog";
import SavedEventInvoiceDocumentModal from "../../Invoicing/ManageInvoices/SavedEventInvoiceDocumentModal";
import { canManageEventInvoiceFinancialActions } from "../../Invoicing/ManageInvoices/manageEventInvoicePermissions";
import {
  CmpEventSalesRouteTarget,
  getCmpEventSalesRouteTarget,
} from "../../Invoicing/invoiceCardCheckoutRouting";
import {
  BillingDetails,
  blackButtonSx,
  blankBillingDetails,
  ConfirmDialog,
  cpaGold,
  disabledFieldSx,
  downloadCsvFile,
  EventSalesFilterBar,
  EventSalesSection,
  EventSalesSortableHeader,
  EventSalesSortState,
  EventSelect,
  EventStatCardRow,
  formatCurrencyFromCents,
  getEventInvoiceDisplayName,
  goldButtonSx,
  salesActionCellButtonsSx,
  isPaidEventPaymentStatus,
  MemberBillingSelector,
  MemberLookupOption,
  ProcessPaymentModal,
  ProcessPaymentSubmit,
  ResultModal,
  splitName,
  toCmpEventBillingInput,
  toCmpEventBillingInputFromSnapshot,
  toggleEventSalesSort,
  UpdateAttendeeNamesModal,
  useDownloadEventInvoicePdf,
  getSponsorshipComplimentaryTicketNote,
  getSponsorshipDraftTaxNote,
  eventCardPaymentReceiptUnavailableMessage,
} from "./eventSalesShared";
import {
  getStaffPaymentMethodOptions,
  useStaffRecordablePaymentMethods,
} from "../../Invoicing/staffPaymentMethods";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";
import {
  completeEventSalesOperation,
  prepareEventSalesOperation,
  runEventSalesOperation,
  runEventSalesSingleFlight,
} from "./eventSalesOperation";
import {
  clearLegacyEventSponsorshipBatchJournals,
  finishStaffSponsorship,
  getStaffSponsorshipErrorMessage,
  runStaffSponsorshipCreate,
  type StaffSponsorshipCreateAttempt,
  type StaffSponsorshipCreateResult,
} from "./eventSponsorshipCreate";
import { getSponsorshipIncludedTicketsHeading } from "./eventAttendeeTicketGroups";
import {
  CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE,
  eventSalesCallerHandledApolloContext,
  eventSalesExportApolloContext,
  eventSalesHostedCheckoutApolloContext,
} from "./eventSalesApolloErrorPolicy";
import { validateEventSalesBilling } from "./eventSalesBilling";
import EventSecureCardCheckoutPanel from "./EventSecureCardCheckoutPanel";
import { getEventSecureCardCheckoutReadback } from "./eventSecureCardCheckout";
import {
  buildCmpEventSponsorshipAttendeeUpdateInputs,
  buildGuardedEventAttendeeUpdateInput,
  getEventSponsorshipAttendeeCount,
  toCmpEventCustomFieldAnswerInputs,
} from "./eventSalesAttendeeUpdates";
import type { EventSalesAttendeeNameEdit } from "./eventSalesAttendeeUpdates";
import EventInvoiceDraftPreview from "./EventInvoiceDraftPreview";
import {
  eventInvoiceSelectableStatusFilterOptions,
  EventInvoiceSelectableStatusFilter,
  eventSaleMatchesStatusFilters,
  getEventInvoiceDocumentMode,
  getEventSaleInvoiceStatusLabel,
} from "./eventInvoiceStatus";
import type { EventSalesPageMode } from "./eventSalesPageMode";
import {
  eventSalesChangesSavedMessage,
  eventSponsorListCsvFilename,
  getEventAttendeeUpdateHelperText,
  getEventSponsorListExportSuccess,
  type EventSalesSuccessMessage,
} from "./eventSalesExports";
import {
  getEventSalesAttendeeEditTarget,
  type EventSalesAttendeeEditTarget,
} from "./eventSalesAttendeeEditRoute";
import {
  getEventPaymentSuccessDetails,
  type EventPaymentSuccessDetails,
} from "./eventPaymentSuccess";
import EventInvoiceAttendeeFields from "./EventInvoiceAttendeeFields";
import { getEventAttendeeChoiceFields } from "../eventBackendMappers";
import {
  getEventAttendeeSuggestions,
  mergeEventAttendeeSuggestions,
} from "./eventAttendeeSuggestions";
import {
  getEventAttendeeCompanyOptions,
  useEventMemberAttendeeSuggestions,
} from "./useEventMemberAttendeeSuggestions";
import EventInvoiceCreateFields from "./EventInvoiceCreateFields";
import EventSalesSearchableMultiSelectMenu from "./EventSalesSearchableMultiSelectMenu";
import {
  emailSavedEventInvoiceDocument,
  type PendingEventInvoiceDocumentEmail,
} from "./eventInvoiceDocumentEmail";
import {
  createEventInvoiceAttendee,
  getDefaultEventInvoiceDates,
  getEventInvoiceDueDateForIssueDate,
  getEventInvoiceAttendeeCustomFieldAnswerInputs,
  isEventInvoiceDateRangeValid,
  isEventInvoiceDateValid,
  normalizeEventInvoiceReference,
  getEventInvoicePrimaryAttendeeId,
  validateEventInvoiceAttendeeFields,
  getEventSponsorshipInvoiceLineDescription,
  type EventInvoiceAttendee,
} from "./eventInvoiceCreateState";
import {
  getSponsorshipInvoiceFormDirtyState,
  shouldConfirmEventSelectionChange,
  shouldRebaseEventInvoiceDateDefaults,
  SponsorshipInvoiceFormDirtyState,
} from "./eventSalesInvoiceFormDirtyState";
import {
  buildStaffCheckFirstFallback,
  buildStaffErrorFallback,
  getGraphQLErrorMessage,
  getUserFacingErrorText,
  hasGraphQLErrorCode,
} from "../../../../utils/userFacingError";
import { DATE_FORMAT_MESSAGE } from "../../../../utils/fieldMessages";
import {
  EVENT_DRAFT_NOT_READ_TEXT,
  EVENT_INVOICE_NOT_FOUND_TEXT,
  EVENT_INVOICE_NOTHING_TO_PAY_TEXT,
  SPONSORSHIP_INVOICE_NOT_FINISHED_TEXT,
} from "./eventSalesText";

const sponsorshipPriceLoadFallback = buildStaffErrorFallback("load the sponsorship price");

type Sponsorship = CmpEventSponsorshipRequestFieldsFragment;

/** Monday D30: the Create Sponsorship Invoice form kept in a saved draft. */
type SponsorshipInvoiceDraftForm = {
  version: number;
  eventId: string;
  soldTo: InvoiceSoldToKind;
  billing: BillingDetails;
  packageId: string;
  attendees: EventInvoiceAttendee[];
  invoiceDate: string;
  dueDate: string;
  referencePurchaseOrder: string;
};

const isInactiveSponsorship = (sponsorship: Sponsorship) =>
  sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Canceled ||
  sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Rejected;

// CMP Dashboard (21) sponsorRows (:19749) and IF11 Q7: sponsorship tickets
// are issued with the sponsorship, paid or not.
const sponsorTicketStatusLabel = (_sponsorship: Sponsorship) => "Issued";

type SponsorSortColumn =
  | "invoiceNumber"
  | "companyName"
  | "invoiceStatus"
  | "sponsorshipType"
  | "tickets"
  | "ticketStatus";

const sponsorshipInvoiceStatusLabel = (
  sponsorship: Sponsorship,
  timeZone?: string | null,
): string => getEventSaleInvoiceStatusLabel(sponsorship, timeZone);

const InvoiceManageSponsorshipSalesContent = ({
  attendeeEditTarget,
  mode,
  routeTarget,
}: {
  attendeeEditTarget: EventSalesAttendeeEditTarget | null;
  mode: EventSalesPageMode;
  routeTarget: CmpEventSalesRouteTarget | null;
}): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const noun = useCmpOrganizationNoun();
  const authenticatedUser = useGetUserFromUserIdInAuth();
  const chamber = authenticatedUser?.chamberInfo;
  const chamberId = chamber?.id || "";
  const canEmailSavedEventDocuments = canManageEventInvoiceFinancialActions({
    enabledPagePermissions:
      authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages || [],
    invoiceKind: CmpInvoiceKindEnum.EventSponsorship,
    userType: authenticatedUser?.userType,
  });
  const pageTitle =
    mode === "create"
      ? "Create Sponsorship Invoice"
      : "Manage Sponsorship Sales";
  const pageDescription =
    mode === "create"
      ? "Complete the information below, to create sponsorship invoice, and assign complimentary tickets if included in the sponsorship level."
      : "Manage all sponsorship invoices for events.";

  const [selectedEventId, setSelectedEventId] = useState("");
  const [pendingSelectedEventId, setPendingSelectedEventId] = useState<
    string | null
  >(null);
  // Success + error feedback surfaces as popup modals (module policy
  // EVT-SALES-T-003) — never header Alerts/toasts.
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    setPageHeaderOverride({
      ...invoicePageLayoutPresentation,
      title: pageTitle,
      description: pageDescription,
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [pageDescription, pageTitle, setPageHeaderOverride]);

  // ----- Queries -------------------------------------------------------------
  const { data: eventsData, loading: eventsLoading } =
    useGetCmpManageEventsQuery({
      variables: { input: { chamberId } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const events = useMemo(
    () => eventsData?.getCmpManageEvents.events || [],
    [eventsData],
  );

  // A linked event is selected once, so clearing the event after Create or
  // after "No Sponsorship Created" does not re-select it.
  const routeEventAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    const focusedEventId = routeTarget?.eventId;

    if (
      focusedEventId &&
      routeEventAppliedRef.current !== focusedEventId &&
      events.some((event) => event.id === focusedEventId)
    ) {
      routeEventAppliedRef.current = focusedEventId;
      setSelectedEventId(focusedEventId);
      setFieldErrors((current) => {
        if (!current.event) {
          return current;
        }
        const next = { ...current };
        delete next.event;
        return next;
      });
    }
  }, [events, routeTarget?.eventId]);

  // Ticket Sales "Update Attendee Details" on a sponsorship row opens this
  // page with the sponsorship's event selected once (see the effect below
  // that opens the editor).
  const attendeeEditEventAppliedRef = useRef(false);
  useEffect(() => {
    const eventId = attendeeEditTarget?.eventId;
    if (
      mode !== "manage" ||
      routeTarget ||
      !eventId ||
      attendeeEditEventAppliedRef.current ||
      !events.some((event) => event.id === eventId)
    ) {
      return;
    }
    attendeeEditEventAppliedRef.current = true;
    setSelectedEventId(eventId);
  }, [attendeeEditTarget?.eventId, events, mode, routeTarget]);

  const selectedEventPaymentRestriction =
    events.find((event) => event.id === selectedEventId)
      ?.paymentActionRequiredReason || null;

  const { data: eventDetailData } = useGetCmpEventDetailQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const loadedEvent = eventDetailData?.getCmpEventDetail.event;
  const eventDetail = loadedEvent?.id === selectedEventId ? loadedEvent : null;
  const sponsorshipPackages = useMemo(
    () => eventDetail?.sponsorshipPackages || [],
    [eventDetail],
  );
  // Design D:11038-11046: choosing an event with sponsorship turned off or no
  // sponsor levels opens "No Sponsorship Created" and clears Select Event.
  // It waits for the chosen event's own detail, so no other event flashes it.
  const [noSponsorshipEventTitle, setNoSponsorshipEventTitle] = useState<string | null>(null);
  useEffect(() => {
    if (mode !== "create" || !eventDetail) return;
    if (eventDetail.sponsorshipEnabled && eventDetail.sponsorshipPackages.length > 0) return;
    setNoSponsorshipEventTitle(eventDetail.title);
    setSelectedEventId("");
  }, [mode, eventDetail]);
  const fallbackTicketTypeId = eventDetail?.tickets[0]?.id || "";

  const { readSponsorships } = useEventSalesReadback();
  const {
    data: sponsorshipsData,
    loading: sponsorshipsLoading,
    refetch: refetchSponsorships,
  } = useGetCmpEventSponsorshipRequestsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const sponsorships = useMemo(
    () =>
      sponsorshipsData?.getCmpEventSponsorshipRequests.sponsorshipRequests ||
      [],
    [sponsorshipsData],
  );
  const { data: registrationsData } = useGetCmpEventRegistrationsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const registrations = useMemo(
    () => registrationsData?.getCmpEventRegistrations.registrations || [],
    [registrationsData],
  );

  // Debounced server-side member search (EVT-BLAIR-SALES-002): the selector
  // debounces typing and hands us the term; we own the query at limit 1000.
  const [memberSearch, setMemberSearch] = useState("");
  const { data: lookupData, loading: lookupsLoading } =
    useGetCmpInvoiceCreateLookupsQuery({
      variables: {
        input: {
          chamberId,
          limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT,
          search: memberSearch.trim() || undefined,
          // This page uses the members only.
          ...MEMBER_ONLY_INVOICE_LOOKUP_PARTS,
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const members: MemberLookupOption[] = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData],
  );
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const chamberTimeZone =
    settingsData?.getChamberMemberProSettings.setup.timeZone;
  const invoicePaymentTerms =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .invoicePaymentTerms;
  // The preview shows the payment columns the issued invoice will show.
  const draftPaymentMethods = useMemo(
    () =>
      getStaffInvoiceDraftPaymentMethods({
        creditCardPaymentsReady: getCmpCreditCardPaymentsReady(
          settingsData?.getChamberMemberProSettings.paymentsAndTaxes
            .stripeConnect,
        ),
        manualPaymentMethods:
          settingsData?.getChamberMemberProSettings.paymentsAndTaxes
            .manualPaymentMethods || [],
      }),
    [settingsData],
  );
  // ----- Mutations -----------------------------------------------------------
  const [createSponsorship, { loading: creating }] =
    useCreateCmpEventSponsorshipRequestMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [approveSponsorship, { loading: approving }] =
    useApproveCmpEventSponsorshipRequestMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [getSponsorshipByOperation] =
    useGetCmpEventSponsorshipRequestByOperationLazyQuery({
      context: eventSalesHostedCheckoutApolloContext,
      fetchPolicy: "network-only",
    });
  const [recordPayment, { loading: recordingPayment }] =
    useRecordCmpEventSponsorshipManualPaymentMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [updateSponsorship, { loading: updatingSponsorship }] =
    useUpdateCmpEventSponsorshipRequestMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [exportSponsors, { loading: exportingSponsors }] =
    useExportCmpEventSponsorsCsvLazyQuery({
      context: eventSalesExportApolloContext,
    });
  const [emailCmpInvoiceDocument, { loading: emailingInvoiceDocument }] =
    useEmailCmpInvoiceDocumentMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const { downloadInvoicePdf, downloadingPdf } =
    useDownloadEventInvoicePdf(chamberId);

  const handleExportSponsors = async () => {
    if (!selectedEventId) {
      return;
    }
    try {
      const result = await exportSponsors({
        variables: { input: { chamberId, eventId: selectedEventId } },
      });
      const csv = result.data?.exportCmpEventSponsorsCsv;
      if (!csv) {
        if (result.error) throw result.error;
        throw new Error("The sponsor export did not return a file.");
      }
      downloadCsvFile({
        csvContent: csv.csvContent,
        contentType: csv.contentType,
        filename: eventSponsorListCsvFilename,
      });
      setSuccessMessage(
        getEventSponsorListExportSuccess(
          events.find((event) => event.id === selectedEventId)?.title ||
            "Event",
        ),
      );
    } catch (error) {
      setErrorModal({
        title: "Sponsor list not exported",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback("export the sponsor list"),
        ),
      });
    }
  };

  // ----- Analytics -----------------------------------------------------------
  const analytics = useMemo(() => {
    let sponsors = 0;
    let paid = 0;
    let unpaid = 0;
    let ticketsAllocated = 0;

    sponsorships.forEach((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return;
      }
      sponsors += 1;
      ticketsAllocated += getEventSponsorshipAttendeeCount(sponsorship);
      if (isPaidEventPaymentStatus(sponsorship.paymentStatus)) {
        paid += 1;
      } else {
        unpaid += 1;
      }
    });

    return {
      sponsors,
      paid,
      unpaid,
      ticketsAllocated,
      revenueInCents: eventDetail?.sponsorshipRevenueInCents || 0,
    };
  }, [sponsorships, eventDetail]);

  // Availability (EVT-SALES-S-004): active (non-canceled/non-rejected)
  // requests hold a slot against each package's configured maxAvailable —
  // the same counting rule as the backend's public availability assert.
  const activePackageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    sponsorships.forEach((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return;
      }
      counts.set(
        sponsorship.sponsorshipPackageId,
        (counts.get(sponsorship.sponsorshipPackageId) || 0) + 1,
      );
    });
    return counts;
  }, [sponsorships]);

  const packageRemaining = (pkg: {
    id: string;
    maxAvailable?: number | null;
  }): number | null =>
    pkg.maxAvailable == null
      ? null
      : Math.max(0, pkg.maxAvailable - (activePackageCounts.get(pkg.id) || 0));

  // ----- Create invoice form -------------------------------------------------
  const [billing, setBilling] = useState<BillingDetails>(blankBillingDetails());
  // Design (11) "Sold To": a sponsorship invoice can bill a non-member
  // customer typed by staff (Stoney Creek, Monday 13162910299).
  const [soldTo, setSoldTo] = useState<InvoiceSoldToKind>("member");
  const [packageId, setPackageId] = useState("");
  const taxExempt = false;
  const [attendees, setAttendees] = useState<EventInvoiceAttendee[]>([]);
  const [pendingCreate, setPendingCreate] = useState(false);
  const memberAttendeeSuggestions = useEventMemberAttendeeSuggestions({
    chamberId,
    memberId: billing.selectedMemberCompanyId,
  });
  const attendeeSuggestions = useMemo(
    () =>
      mergeEventAttendeeSuggestions(
        memberAttendeeSuggestions,
        getEventAttendeeSuggestions({
          chamberId,
          eventId: selectedEventId,
          registrations,
          selectedMemberCompanyId: billing.selectedMemberCompanyId,
          sponsorships,
        }),
      ),
    [
      billing.selectedMemberCompanyId,
      chamberId,
      memberAttendeeSuggestions,
      registrations,
      selectedEventId,
      sponsorships,
    ],
  );
  const attendeeCompanyOptions = useMemo(
    () => getEventAttendeeCompanyOptions(members),
    [members],
  );
  const [initialInvoiceDates] = useState(() =>
    getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    }),
  );
  const [invoiceDate, setInvoiceDate] = useState(
    initialInvoiceDates.invoiceDate,
  );
  const [dueDate, setDueDate] = useState(initialInvoiceDates.dueDate);
  const [referencePurchaseOrder, setReferencePurchaseOrder] = useState("");
  const [createFormBaseline, setCreateFormBaseline] =
    useState<SponsorshipInvoiceFormDirtyState>(() =>
      getSponsorshipInvoiceFormDirtyState({
        attendees,
        billing,
        dueDate,
        invoiceDate,
        packageId,
        referencePurchaseOrder,
        taxExempt,
      }),
    );
  const [previewOpen, setPreviewOpen] = useState(false);
  // Monday D30: Save Draft and reopening a saved draft (?draftInvoiceId=).
  const navigate = useNavigate();
  const eventInvoiceDraftRoute = useEventInvoiceDraftFromRoute({
    chamberId,
    enabled: mode === "create",
  });

  // A saved draft named in the address that cannot open shows why, instead
  // of /500 or a silent empty form (audit 3.1 item 4).
  const draftRouteError = eventInvoiceDraftRoute.error;
  useEffect(() => {
    if (!draftRouteError) return;
    setErrorModal({
      title: "Draft not opened",
      message: getUserFacingErrorText(
        draftRouteError,
        buildStaffErrorFallback("open the saved draft"),
      ),
    });
  }, [draftRouteError]);
  const [activeDraft, setActiveDraft] = useState<{
    id: string;
    updatedAtUtcMilli: number;
  } | null>(null);
  const [draftSavedCustomerName, setDraftSavedCustomerName] = useState<
    string | null
  >(null);
  const draftHydrationRef = useRef<{
    id: string;
    form: SponsorshipInvoiceDraftForm;
    packageApplied: boolean;
  } | null>(null);
  const [saveEventInvoiceDraft, { loading: savingDraft }] =
    useSaveCmpEventInvoiceDraftMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const [createdInvoice, setCreatedInvoice] = useState<{
    eventId: string;
    invoiceId: string;
    recipientEmail: string | null;
    sponsorshipRequestId: string;
  } | null>(null);
  const [createdInvoiceActionError, setCreatedInvoiceActionError] = useState<
    string | null
  >(null);
  const createSponsorshipInFlightRef = useRef(false);
  // One create operation ID per attempt, in memory only (see
  // eventSponsorshipCreate.ts). Never stored in the browser.
  const sponsorshipCreateAttemptRef =
    useRef<StaffSponsorshipCreateAttempt | null>(null);
  const [sponsorshipToFinish, setSponsorshipToFinish] = useState<{
    error: string | null;
    eventId: string;
    sponsorship: CmpEventSponsorshipRequestFieldsFragment;
  } | null>(null);
  useEffect(() => {
    // Old tabs stored a sponsorship batch journal in this browser tab. It is
    // no longer read; remove it so stale data cannot linger.
    clearLegacyEventSponsorshipBatchJournals();
  }, []);
  // Field-level validation errors render in red under the offending
  // field/region (mirrors B8's fieldErrors approach) — no header banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] =
    useState<EventSalesSuccessMessage | null>(null);
  const [paymentSuccess, setPaymentSuccess] =
    useState<EventPaymentSuccessDetails | null>(null);
  const [pendingDocumentEmail, setPendingDocumentEmail] =
    useState<PendingEventInvoiceDocumentEmail | null>(null);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(
    null,
  );
  const paymentSuccessInvoiceId = paymentSuccess?.invoiceId.trim() || null;
  const { data: paymentSuccessInvoiceDetailData } = useGetCmpInvoiceDetailQuery(
    {
      fetchPolicy: "network-only",
      skip: !chamberId || !paymentSuccessInvoiceId,
      variables: {
        input: {
          chamberId,
          invoiceId: paymentSuccessInvoiceId || "CMP_SKIPPED",
        },
      },
    },
  );
  const paymentSuccessInvoiceDetail =
    paymentSuccessInvoiceDetailData?.getCmpInvoiceDetail || null;
  const canEmailPaymentSuccessReceipt = Boolean(
    paymentSuccess &&
    paymentSuccessInvoiceDetail?.invoice.id === paymentSuccess.invoiceId &&
    paymentSuccessInvoiceDetail.availableActions.some(
      ({ action, available }) =>
        action === CmpInvoiceActionTypeEnum.EmailDocument && available,
    ) &&
    paymentSuccessInvoiceDetail.payments.some(
      (payment) =>
        payment.id === paymentSuccess.paymentId &&
        isReceiptEligiblePaymentStatus(payment.status),
    ),
  );

  const clearFieldErrors = (...keys: string[]) =>
    setFieldErrors((current) => {
      if (!keys.some((key) => current[key])) {
        return current;
      }
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const selectedPackage = sponsorshipPackages.find(
    (pkg) => pkg.id === packageId,
  );
  const includedQuantity = selectedPackage?.includedAttendeeQuantity || 0;
  const selectedRemaining = selectedPackage
    ? packageRemaining(selectedPackage)
    : null;

  useEffect(() => {
    // A reopened draft keeps its saved dates.
    if (activeDraft) return;
    if (
      !shouldRebaseEventInvoiceDateDefaults({
        baseline: createFormBaseline,
        current: { dueDate, invoiceDate },
      })
    ) {
      return;
    }

    const defaults = getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    });
    if (defaults.invoiceDate === invoiceDate && defaults.dueDate === dueDate) {
      return;
    }

    setInvoiceDate(defaults.invoiceDate);
    setDueDate(defaults.dueDate);
    setCreateFormBaseline((current) => ({
      ...current,
      dueDate: defaults.dueDate,
      invoiceDate: defaults.invoiceDate,
    }));
  }, [
    activeDraft,
    chamberTimeZone,
    createFormBaseline,
    dueDate,
    invoiceDate,
    invoicePaymentTerms,
  ]);

  // One sponsorship package creates one request and one invoice. Its saved
  // included-attendee quantity controls the exact number of attendee rows.
  useEffect(() => {
    const target = Math.max(0, includedQuantity);
    setAttendees((current) => {
      if (target === 0) {
        return [];
      }
      if (target <= current.length) {
        return current.slice(0, target);
      }
      const additions = Array.from({ length: target - current.length }, () =>
        createEventInvoiceAttendee(),
      );
      return [...current, ...additions];
    });
  }, [includedQuantity]);

  // Reopen a saved draft: the form first, then the level and attendees once
  // the draft event is loaded.
  useEffect(() => {
    const draft = eventInvoiceDraftRoute.draft;
    if (!draft || draftHydrationRef.current?.id === draft.id) return;
    const form = parseEventInvoiceDraftForm<SponsorshipInvoiceDraftForm>(
      draft.eventInvoiceDraftForm,
    );
    if (!form || !form.eventId) {
      setErrorModal({
        title: "The draft could not be opened",
        message:
          EVENT_DRAFT_NOT_READ_TEXT,
      });
      return;
    }
    draftHydrationRef.current = { id: draft.id, form, packageApplied: false };
    setActiveDraft({ id: draft.id, updatedAtUtcMilli: draft.updatedAtUtcMilli || 0 });
    setSelectedEventId(form.eventId);
    setSoldTo(form.soldTo);
    setBilling(form.billing);
    setInvoiceDate(form.invoiceDate);
    setDueDate(form.dueDate);
    setReferencePurchaseOrder(form.referencePurchaseOrder);
  }, [eventInvoiceDraftRoute.draft]);

  useEffect(() => {
    const pending = draftHydrationRef.current;
    if (!pending || pending.packageApplied || eventDetail?.id !== pending.form.eventId) {
      return;
    }
    pending.packageApplied = true;
    const { form } = pending;
    setPackageId(form.packageId);
    setAttendees(form.attendees);
    setCreateFormBaseline(
      getSponsorshipInvoiceFormDirtyState({
        attendees: form.attendees,
        billing: form.billing,
        dueDate: form.dueDate,
        invoiceDate: form.invoiceDate,
        packageId: form.packageId,
        referencePurchaseOrder: form.referencePurchaseOrder,
        taxExempt,
      }),
    );
    if (eventInvoiceDraftRoute.openPreview) setPreviewOpen(true);
  }, [eventDetail?.id, eventInvoiceDraftRoute.openPreview, taxExempt]);

  const [loadPricing] = useGetCmpStaffEventSponsorshipPricingPreviewLazyQuery({
    fetchPolicy: "network-only",
    // A failed price load shows under the form, not on /500 (audit 3.1 item 4).
    context: eventSalesCallerHandledApolloContext,
  });
  const [pricing, setPricing] = useState<GetCmpStaffEventSponsorshipPricingPreviewQuery["getCmpStaffEventSponsorshipPricingPreview"] | null>(null);
  const [pricingError, setPricingError] = useState("");
  useEffect(() => {
    let current = true;
    setPricing(null);
    setPricingError("");
    if (mode === "create" && eventDetail && selectedPackage) {
      void loadPricing({ variables: { input: { chamberId, eventId: eventDetail.id,
        sponsorshipPackageId: selectedPackage.id, kind: CmpPublicEventPricingPreviewKindEnum.Sponsorship,
        paymentChoice: { type: CmpPaymentChoiceTypeEnum.Manual } } } }).then(result => {
        if (!current) return;
        if (result.error || !result.data?.getCmpStaffEventSponsorshipPricingPreview) {
          setPricingError(getUserFacingErrorText(result.error, sponsorshipPriceLoadFallback));
        } else setPricing(result.data.getCmpStaffEventSponsorshipPricingPreview);
      }).catch(error => { if (current) setPricingError(getUserFacingErrorText(error, sponsorshipPriceLoadFallback)); });
    }
    return () => { current = false; };
  }, [chamberId, eventDetail, selectedPackage, mode, loadPricing]);
  const unitPriceInCents = selectedPackage?.priceInCents || 0;
  const lineSubtotalInCents = pricing?.subtotalInCents ?? unitPriceInCents;
  const lineTaxInCents = pricing?.taxAmountInCents ?? 0;
  const lineTotalInCents = pricing?.totalAmountInCents ?? lineSubtotalInCents;
  const taxRateLabel = pricing?.taxRateLabel || "Tax";
  const taxRatePercentage = pricing?.taxRatePercentage ?? 0;

  const includedAttendeeTicketTypeId =
    selectedPackage?.includedAttendeeTicketTypeId || fallbackTicketTypeId;
  const attendeeChoiceFields = useMemo(
    () =>
      getEventAttendeeChoiceFields({
        customFields: eventDetail?.customFields || [],
        ticketTypeId: includedAttendeeTicketTypeId,
      }),
    [eventDetail, includedAttendeeTicketTypeId],
  );

  const resetCreateForm = () => {
    const nextBilling = blankBillingDetails();
    const defaults = getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    });

    setBilling(nextBilling);
    setSoldTo("member");
    setPackageId("");
    setAttendees([]);
    setInvoiceDate(defaults.invoiceDate);
    setDueDate(defaults.dueDate);
    setReferencePurchaseOrder("");
    setCreateFormBaseline(
      getSponsorshipInvoiceFormDirtyState({
        attendees: [],
        billing: nextBilling,
        dueDate: defaults.dueDate,
        invoiceDate: defaults.invoiceDate,
        packageId: "",
        referencePurchaseOrder: "",
        taxExempt: false,
      }),
    );
    setFieldErrors({});
  };

  const handleBillingChange = (next: BillingDetails) => {
    setBilling(next);
  };

  // Switching Sold To clears the member or customer and the Bill-To details,
  // so a member's address never carries over to a non-member invoice.
  const handleSoldToChange = (next: InvoiceSoldToKind) => {
    if (next === soldTo) {
      return;
    }
    setSoldTo(next);
    setBilling(blankBillingDetails());
    clearFieldErrors(
      "member",
      "companyName",
      "billingStreetAddress",
      "billingCity",
      "billingProvince",
      "billingPostalCode",
      "billingCountry",
      "billingContactName",
      "billingEmail",
      "billingPhone",
    );
  };

  const handleSelectPackage = (id: string) => {
    setPackageId(id);
    clearFieldErrors("sponsorshipLevel");
  };

  const handleAttendeesChange = (next: EventInvoiceAttendee[]) => {
    setAttendees(next);
  };

  // Field-level validation (mirrors B8) — errors render in red under the
  // offending field/region, never as a header banner.
  const validateCreate = (): Record<string, string> => {
    const errors: Record<string, string> = validateEventSalesBilling({
      billing,
      companyNameError: "Customer / Company Name is required",
      requireCompanyName: soldTo === "non-member",
    });
    if (!selectedEventId) {
      errors.event = "Please select an event.";
    }
    if (soldTo === "member" && !billing.selectedMemberId) {
      errors.member = "Member is required";
    }
    if (!invoiceDate) {
      errors.invoiceDate = "Invoice Date is required";
    } else if (!isEventInvoiceDateValid(invoiceDate)) {
      errors.invoiceDate = DATE_FORMAT_MESSAGE;
    }
    if (!dueDate) {
      errors.dueDate = "Due Date is required";
    } else if (!isEventInvoiceDateValid(dueDate)) {
      errors.dueDate = DATE_FORMAT_MESSAGE;
    } else if (!isEventInvoiceDateRangeValid({ dueDate, invoiceDate })) {
      errors.dueDate = "Due Date must be on or after Invoice Date.";
    }
    if (selectedPackage && !pricing) errors.sponsorshipLevel = pricingError || "Wait for the sponsorship price to load.";
    if (!selectedPackage) {
      errors.sponsorshipLevel = "Please select a sponsorship level.";
    } else if (selectedRemaining != null && selectedRemaining <= 0) {
      errors.sponsorshipLevel = "This sponsorship level is sold out.";
    } else if (includedQuantity > 0 && !includedAttendeeTicketTypeId) {
      errors.sponsorshipLevel =
        "This sponsorship level includes free tickets, but the event has no No Charge Sponsorship Ticket (NCST). In Events & Sponsorships > Manage Events, open Actions > Edit Event. Then add a ticket with the code NCST in Ticket - Set Up.";
    }
    Object.assign(
      errors,
      validateEventInvoiceAttendeeFields({
        attendees,
        // The first included attendee is attendee 1 for the sponsorship.
        emailRequiredAttendeeId: getEventInvoicePrimaryAttendeeId([attendees]),
        errorKeyPrefix: "attendees",
      }),
    );
    return errors;
  };

  const handleOpenDraftPreview = () => {
    const errors = validateCreate();
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      setPreviewOpen(true);
      return;
    }
    scrollToFirstInvalidField();
  };

  const approveSavedSponsorship = async ({
    eventId,
    sponsorship,
  }: {
    eventId: string;
    sponsorship: CmpEventSponsorshipRequestFieldsFragment;
  }): Promise<CmpEventSponsorshipRequestFieldsFragment> => {
    const approvalOperation = await prepareEventSalesOperation({
      action: "APPROVE_SPONSORSHIP",
      chamberId,
      entityId: sponsorship.id,
      eventId,
      input: {
        chamberId,
        ...(activeDraft ? {
          draftInvoiceId: activeDraft.id,
          draftExpectedUpdatedAtUtcMilli: activeDraft.updatedAtUtcMilli,
        } : {}),
        eventId,
        paymentChoice: {
          type: CmpPaymentChoiceTypeEnum.Manual,
          emailCommunicationConsentConfirmed: true,
        },
        sponsorshipRequestId: sponsorship.id,
      },
    });
    const approved = await approveSponsorship({
      variables: { input: approvalOperation.input },
    });
    const approvalPayload = approved.data?.approveCmpEventSponsorshipRequest;
    const approvedSponsorship = approvalPayload?.sponsorshipRequest;

    if (
      !approvalPayload?.sponsorshipRequestSavedSuccessfully ||
      approvedSponsorship?.id !== sponsorship.id ||
      !approvedSponsorship?.invoice?.id
    ) {
      throw new Error(
        "The sponsorship invoice could not be confirmed after approval.",
      );
    }

    completeEventSalesOperation({ operation: approvalOperation });
    return approvedSponsorship;
  };

  const lookUpSponsorshipByOperation = async ({
    eventId,
    operationId,
  }: {
    eventId: string;
    operationId: string;
  }) => {
    const lookup = await getSponsorshipByOperation({
      variables: { input: { chamberId, eventId, operationId } },
    });

    if (lookup.error || !lookup.data) {
      throw (
        lookup.error ||
        new Error("The saved sponsorship could not be checked.")
      );
    }

    return lookup.data.getCmpEventSponsorshipRequestByOperation || null;
  };

  const showCreatedSponsorshipInvoice = async ({
    eventId,
    sponsorship,
  }: {
    eventId: string;
    sponsorship: CmpEventSponsorshipRequestFieldsFragment;
  }) => {
    if (!sponsorship.invoice?.id) {
      return;
    }

    sponsorshipCreateAttemptRef.current = null;
    setSponsorshipToFinish(null);
    // Design D:10057-10073: reset the whole form, including Select Event,
    // and return to the top; dates restart at today and payment terms.
    resetCreateForm();
    setSelectedEventId("");
    if (activeDraft) {
      setActiveDraft(null);
      draftHydrationRef.current = null;
      navigate(RoutePaths.chamberAdmin_events_sponsorshipSalesCreate, { replace: true });
    }
    scrollDashboardToTop();
    setCreatedInvoice({
      eventId,
      invoiceId: sponsorship.invoice.id,
      recipientEmail: getSavedInvoiceRecipientEmail(sponsorship.invoice),
      sponsorshipRequestId: sponsorship.id,
    });
    setCreatedInvoiceActionError(null);
    await refetchSponsorships();
  };

  const handleSponsorshipCreateResult = async ({
    eventId,
    result,
  }: {
    eventId: string;
    result: StaffSponsorshipCreateResult<CmpEventSponsorshipRequestFieldsFragment>;
  }) => {
    switch (result.kind) {
      case "invoiced":
        await showCreatedSponsorshipInvoice({
          eventId,
          sponsorship: result.sponsorship,
        });
        return;
      case "finish":
        // The sponsorship is saved: offer to finish its invoice instead of
        // creating a second sponsorship.
        setSponsorshipToFinish({
          error: result.error
            ? getStaffSponsorshipErrorMessage(result.error, "")
            : null,
          eventId,
          sponsorship: result.sponsorship,
        });
        await refetchSponsorships();
        return;
      case "refused": {
        const message = getStaffSponsorshipErrorMessage(
          result.error,
          buildStaffErrorFallback("create the sponsorship invoice"),
        );
        // Route by code first; the old word match stays as a fallback.
        if (
          hasGraphQLErrorCode(result.error, CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE) ||
          /(capacity|sold out|available|remaining|inventory)/i.test(getGraphQLErrorMessage(result.error) || "")
        ) {
          setFieldErrors((current) => ({
            ...current,
            sponsorshipLevel: message,
          }));
          return;
        }
        setErrorModal({ title: "Sponsorship invoice not created", message });
        return;
      }
      case "notSaved": {
        const reason = getStaffSponsorshipErrorMessage(result.error, "");
        setErrorModal({
          title: "Sponsorship not saved",
          message: reason
            ? `${reason} The sponsorship was not saved. Check the details and try again.`
            : buildStaffErrorFallback("save the sponsorship", "The sponsorship was not saved. Check the details."),
        });
        return;
      }
      case "unconfirmed":
        setErrorModal({
          title: "Sponsorship not confirmed",
          message:
            "We could not confirm that the sponsorship was saved. Check your internet connection, then click Create Invoice again. Chamber Member Pro checks for the saved sponsorship first, so it is not created twice.",
        });
    }
  };

  const handleFinishSponsorshipInvoice = async () => {
    if (!sponsorshipToFinish) {
      return;
    }

    const { eventId, sponsorship } = sponsorshipToFinish;
    const result = await finishStaffSponsorship({
      approve: (savedSponsorship) =>
        approveSavedSponsorship({ eventId, sponsorship: savedSponsorship }),
      sponsorship,
    });

    if (result.kind === "invoiced") {
      await showCreatedSponsorshipInvoice({
        eventId,
        sponsorship: result.sponsorship,
      });
      return;
    }

    setSponsorshipToFinish({
      error:
        result.kind === "finish" && result.error
          ? getStaffSponsorshipErrorMessage(
              result.error,
              SPONSORSHIP_INVOICE_NOT_FINISHED_TEXT,
            )
          : SPONSORSHIP_INVOICE_NOT_FINISHED_TEXT,
      eventId,
      sponsorship,
    });
  };

  const executeInvoiceSponsorship = async () => {
    const errors = validateCreate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstInvalidField();
      return;
    }

    const purchaser = splitName(
      billing.billingContactName || billing.customerName,
    );

    setPendingCreate(true);
    const createEventId = selectedEventId;
    try {
      const includedAttendeeInputs = attendees.map((attendee) => {
        const attendeeName = splitName(attendee.name);

        return {
          customFieldAnswers: getEventInvoiceAttendeeCustomFieldAnswerInputs({
            attendee,
          }),
          email: attendee.email.trim(),
          firstName: attendeeName.first,
          lastName: attendeeName.last || undefined,
          phone: attendee.phone.trim() || undefined,
          companyName: attendee.companyName?.trim() || undefined,
          staffNotes: attendee.staffNotes?.trim() || undefined,
          ticketTypeId: includedAttendeeTicketTypeId,
        };
      });
      const sponsorshipInput = {
        billing: toCmpEventBillingInput(billing),
        chamberId,
        eventId: createEventId,
        includedAttendees:
          includedQuantity > 0 && includedAttendeeTicketTypeId
            ? includedAttendeeInputs
            : undefined,
        invoiceDetails: {
          dueDate,
          invoiceDate,
          referencePurchaseOrder:
            normalizeEventInvoiceReference(referencePurchaseOrder) || undefined,
        },
        invoiceTaxExempt: false,
        purchaserEmail: billing.billingEmail,
        purchaserFirstName: purchaser.first || billing.customerName,
        purchaserLastName: purchaser.last || "-",
        purchaserPhone: billing.billingPhone || undefined,
        selectedMemberCompanyId: billing.selectedMemberCompanyId || undefined,
        selectedMemberCompanyNameSnapshot: billing.customerName || undefined,
        sponsorDisplayName: billing.customerName,
        sponsorshipPackageId: packageId,
        expectedEventUpdatedAtUtcMilli: eventDetail?.updatedAtUtcMilli,
      };
      const run = await runStaffSponsorshipCreate<
        typeof sponsorshipInput,
        CmpEventSponsorshipRequestFieldsFragment
      >({
        approve: (sponsorship) =>
          approveSavedSponsorship({ eventId: createEventId, sponsorship }),
        attempt: sponsorshipCreateAttemptRef.current,
        create: async (input) => {
          const created = await createSponsorship({ variables: { input } });
          const createPayload = created.data?.createCmpEventSponsorshipRequest;

          return createPayload?.sponsorshipRequestSavedSuccessfully &&
            createPayload.sponsorshipRequest?.id
            ? createPayload.sponsorshipRequest
            : null;
        },
        input: sponsorshipInput,
        lookup: ({ eventId, operationId }) =>
          lookUpSponsorshipByOperation({ eventId, operationId }),
      });
      sponsorshipCreateAttemptRef.current = run.attempt;
      await handleSponsorshipCreateResult({
        eventId: createEventId,
        result: run.result,
      });
    } catch (error) {
      setErrorModal({
        title: "Sponsorship invoice not created",
        message: getStaffSponsorshipErrorMessage(
          error,
          buildStaffErrorFallback("create the sponsorship invoice"),
        ),
      });
    } finally {
      setPendingCreate(false);
    }
  };

  const handleInvoiceSponsorship = async () => {
    await runEventSalesSingleFlight({
      inFlightGuard: createSponsorshipInFlightRef,
      execute: executeInvoiceSponsorship,
    });
  };

  // ----- Manage list: filters ------------------------------------------------
  const [search, setSearch] = useState(routeTarget?.invoiceNumber || "");
  const [invoiceStatusFilters, setInvoiceStatusFilters] = useState<
    EventInvoiceSelectableStatusFilter[]
  >([]);
  const [packageFilters, setPackageFilters] = useState<string[]>([]);
  const [invoiceStatusMenuAnchor, setInvoiceStatusMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [packageMenuAnchor, setPackageMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [sort, setSort] =
    useState<EventSalesSortState<SponsorSortColumn> | null>(null);

  useEffect(() => {
    if (routeTarget?.invoiceNumber) {
      setSearch(routeTarget.invoiceNumber);
      setInvoiceStatusFilters([]);
    }
  }, [routeTarget?.invoiceNumber]);

  const clearSponsorshipFilters = () => {
    setSearch("");
    setInvoiceStatusFilters([]);
    setPackageFilters([]);
  };

  const filteredSponsorships = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sponsorships.filter((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return false;
      }
      if (term) {
        const isFocusedSource = routeTarget?.sourceId === sponsorship.id;
        const haystack = [
          sponsorship.invoice?.invoiceNumber || "",
          sponsorship.sponsorDisplayName,
          sponsorship.selectedMemberCompanyNameSnapshot || "",
          sponsorship.purchaserFirstName,
          sponsorship.purchaserLastName,
          sponsorship.purchaserEmail,
        ]
          .join(" ")
          .toLowerCase();
        if (!isFocusedSource && !haystack.includes(term)) {
          return false;
        }
      }
      if (
        !eventSaleMatchesStatusFilters(
          sponsorship,
          invoiceStatusFilters,
          chamberTimeZone,
        )
      ) {
        return false;
      }
      if (
        packageFilters.length > 0 &&
        !packageFilters.includes(sponsorship.sponsorshipPackageId)
      ) {
        return false;
      }
      return true;
    });
  }, [
    sponsorships,
    routeTarget?.sourceId,
    search,
    invoiceStatusFilters,
    packageFilters,
    chamberTimeZone,
  ]);

  // Real asc/desc sorting on every column except Actions (the previous
  // decorative sort affordances were a lie in the UI — B9-06).
  const sortedSponsorships = useMemo(() => {
    if (!sort) {
      return filteredSponsorships;
    }
    if (sort.column === "invoiceNumber") {
      return sortRowsByInvoiceNumber({
        direction: sort.direction,
        getInvoiceNumber: (sponsorship) =>
          sponsorship.invoice?.invoiceNumber || "",
        rows: filteredSponsorships,
      });
    }
    const sortValue = (sponsorship: Sponsorship): string | number => {
      switch (sort.column) {
        case "companyName":
          return (
            sponsorship.selectedMemberCompanyNameSnapshot ||
            sponsorship.sponsorDisplayName
          ).toLowerCase();
        case "invoiceStatus":
          return sponsorshipInvoiceStatusLabel(
            sponsorship,
            chamberTimeZone,
          ).toLowerCase();
        case "sponsorshipType":
          return sponsorship.sponsorshipPackageNameSnapshot.toLowerCase();
        case "tickets":
          return getEventSponsorshipAttendeeCount(sponsorship);
        case "ticketStatus":
          return sponsorTicketStatusLabel(sponsorship).toLowerCase();
        default:
          return "";
      }
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filteredSponsorships].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA < valueB) {
        return -1 * factor;
      }
      if (valueA > valueB) {
        return 1 * factor;
      }
      return 0;
    });
  }, [chamberTimeZone, filteredSponsorships, sort]);

  const toggleSort = (column: SponsorSortColumn) =>
    setSort((current) => toggleEventSalesSort(current, column));

  const renderSortableHeader = (label: string, column: SponsorSortColumn) => (
    <EventSalesSortableHeader
      label={label}
      column={column}
      sort={sort}
      onToggle={toggleSort}
    />
  );

  // ----- Process payment (Open Invoice) --------------------------------------
  const [paymentTarget, setPaymentTarget] = useState<Sponsorship | null>(null);
  const [paymentConfirmationTarget, setPaymentConfirmationTarget] =
    useState<Sponsorship | null>(null);
  const [documentTarget, setDocumentTarget] = useState<Sponsorship | null>(
    null,
  );
  const [documentModeOverride, setDocumentModeOverride] = useState<
    "INVOICE" | "PAYMENT_RECEIPT" | null
  >(null);
  const currentPaymentTarget = useMemo(
    () =>
      paymentTarget
        ? sponsorships.find(
            (sponsorship) => sponsorship.id === paymentTarget.id,
          ) || paymentTarget
        : null,
    [paymentTarget, sponsorships],
  );
  const currentPaymentRules = currentPaymentTarget?.invoice?.paymentRules;
  const { recordableMethods: staffRecordablePaymentMethods } =
    useStaffRecordablePaymentMethods({
      chamberId,
      skip: !currentPaymentTarget,
    });
  const currentManualPaymentMethods = useMemo(
    () =>
      getStaffPaymentMethodOptions({
        paymentRules: currentPaymentRules,
        recordableMethods: staffRecordablePaymentMethods,
      }),
    [currentPaymentRules, staffRecordablePaymentMethods],
  );
  const currentPaymentUnavailableReason =
    currentPaymentTarget?.paymentActionUnavailableReason ||
    selectedEventPaymentRestriction ||
    (!currentPaymentRules
      ? "We could not check which payment options are available. Refresh the page and try again."
      : null);
  const currentDocumentTarget = useMemo(
    () =>
      documentTarget
        ? sponsorships.find(
            (sponsorship) => sponsorship.id === documentTarget.id,
          ) || documentTarget
        : null,
    [documentTarget, sponsorships],
  );
  const openInvoice = (sponsorship: Sponsorship) => {
    setDocumentModeOverride(null);
    if (sponsorship.invoice) {
      setDocumentTarget(sponsorship);
    }
  };

  const handleRecordPayment = async (payload: ProcessPaymentSubmit) => {
    if (!paymentTarget || !paymentTarget.invoice) {
      return;
    }
    try {
      const result = await runEventSalesOperation({
        action: "RECORD_SPONSORSHIP_MANUAL_PAYMENT",
        chamberId,
        entityId: paymentTarget.id,
        eventId: selectedEventId,
        input: {
          chamberId,
          eventId: selectedEventId,
          sponsorshipRequestId: paymentTarget.id,
          invoiceId: paymentTarget.invoice.id,
          amountInCents: payload.amountInCents,
          manualPaymentMethodId: payload.manualPaymentMethodId,
          receivedAtUtcMilli: payload.receivedAtUtcMilli,
          reference: payload.reference || undefined,
          note: payload.note || undefined,
        },
        execute: (input) =>
          recordPayment({
            variables: { input },
          }),
        isAuthoritativelyCompleted: (mutationResult) => {
          const paymentResult =
            mutationResult.data?.recordCmpEventSponsorshipManualPayment;

          return Boolean(
            paymentResult?.sponsorshipRequestSavedSuccessfully &&
            paymentResult.sponsorshipRequest?.id === paymentTarget.id,
          );
        },
      });
      const savedSponsorship =
        result.data?.recordCmpEventSponsorshipManualPayment.sponsorshipRequest;
      if (
        !result.data?.recordCmpEventSponsorshipManualPayment
          .sponsorshipRequestSavedSuccessfully ||
        savedSponsorship?.id !== paymentTarget.id
      ) {
        throw new Error("The sponsorship payment could not be confirmed.");
      }
      const successDetails = getEventPaymentSuccessDetails(
        savedSponsorship,
        paymentTarget.billingSnapshot?.customerName ||
          paymentTarget.sponsorDisplayName ||
          paymentTarget.selectedMemberCompanyNameSnapshot ||
          "Customer",
      );
      if (!successDetails) {
        // The payment is saved; only the receipt is missing.
        setErrorModal({
          title: "Payment recorded",
          message:
            "The payment was recorded, but we could not show the receipt. Do not record it again. Refresh the page and check the invoice status.",
        });
        return;
      }
      setPaymentTarget(null);
      setPaymentSuccess(successDetails);
      const refreshWarning = await refreshAfterConfirmedSave({
        refresh: () =>
          readSponsorships({ chamberId, eventId: selectedEventId }),
        savedWhat: "The payment was recorded",
      });
      if (refreshWarning) {
        setErrorModal({ title: "Payment recorded", message: refreshWarning });
      }
    } catch (error) {
      // An unknown result may have saved, so staff check before a retry.
      setErrorModal({
        title: "Payment not confirmed",
        message: getUserFacingErrorText(
          error,
          buildStaffCheckFirstFallback(
            "confirm the payment",
            "Refresh the page and check the invoice status",
          ),
        ),
      });
    }
  };

  // ----- Update attendee details ---------------------------------------------
  const [editTarget, setEditTarget] = useState<Sponsorship | null>(null);
  // Update Attendee Details lists the edited sale's member's people (IF11 EV7).
  const editAttendeeSuggestions = useEventMemberAttendeeSuggestions({
    chamberId,
    memberId: editTarget?.selectedMemberCompanyId,
  });
  const [editAttendees, setEditAttendees] = useState<
    EventSalesAttendeeNameEdit[]
  >([]);

  const openEditAttendees = (sponsorship: Sponsorship) => {
    setEditTarget(sponsorship);
    setEditAttendees(
      [
        ...sponsorship.includedAttendees,
        ...(sponsorship.additionalAttendees || []),
      ].map((attendee) => ({
        customFieldAnswers: Object.fromEntries(
          attendee.customFieldAnswers.map(({ fieldId, value }) => [
            fieldId,
            value,
          ]),
        ),
        email: attendee.email || "",
        id: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName || "",
        phone: attendee.phone || "",
        companyName: attendee.companyName || "",
        staffNotes: attendee.staffNotes || "",
        ticketTypeId: attendee.ticketTypeId,
      })),
    );
  };

  const attendeeEditEventId = attendeeEditTarget?.eventId || "";
  const attendeeEditSourceId = attendeeEditTarget?.sourceId || "";
  const attendeeEditOpenedRef = useRef(false);
  useEffect(() => {
    if (
      mode !== "manage" ||
      !attendeeEditEventId ||
      !attendeeEditSourceId ||
      attendeeEditOpenedRef.current ||
      selectedEventId !== attendeeEditEventId
    ) {
      return;
    }
    const sponsorship = sponsorships.find(
      (candidate) =>
        candidate.id === attendeeEditSourceId &&
        candidate.eventId === attendeeEditEventId,
    );
    if (!sponsorship) {
      return;
    }
    attendeeEditOpenedRef.current = true;
    openEditAttendees(sponsorship);
    // openEditAttendees only sets local editor state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeeEditEventId, attendeeEditSourceId, mode, selectedEventId, sponsorships]);

  const handleSaveAttendeeNames = async () => {
    if (!editTarget) return;
    try {
      const input = {
        chamberId,
        eventId: selectedEventId,
        sponsorshipRequestId: editTarget.id,
        sponsorshipPackageId: editTarget.sponsorshipPackageId,
        selectedMemberCompanyId:
          editTarget.selectedMemberCompanyId || undefined,
        selectedMemberCompanyNameSnapshot:
          editTarget.selectedMemberCompanyNameSnapshot || undefined,
        sponsorDisplayName: editTarget.sponsorDisplayName,
        sponsorLogoUrl: editTarget.sponsorLogoUrl || undefined,
        sponsorLogoLink: editTarget.sponsorLogoLink || undefined,
        purchaserFirstName: editTarget.purchaserFirstName,
        purchaserLastName: editTarget.purchaserLastName,
        purchaserEmail: editTarget.purchaserEmail,
        purchaserPhone: editTarget.purchaserPhone || undefined,
        billing: toCmpEventBillingInputFromSnapshot(
          editTarget.billingSnapshot || editTarget.invoice?.billingSnapshot,
        ),
        status: editTarget.status,
        ...buildCmpEventSponsorshipAttendeeUpdateInputs({
          sponsorship: editTarget,
          edits: editAttendees,
        }),
        customFieldAnswers: toCmpEventCustomFieldAnswerInputs({
          answers: editTarget.customFieldAnswers,
        }),
      };
      // Claude Design (21) never offers Delete in Update Attendee Details
      // (CMP Dashboard.dc.html:18797); IF11 EV1 Q1.
      const requiresGuardedUpdate =
        [...editTarget.includedAttendees, ...(editTarget.additionalAttendees || [])].some(({ status }) => status === CmpEventRegistrationStatusEnum.Canceled);
      const prepared = requiresGuardedUpdate ? await prepareEventSalesOperation({
        action: "UPDATE_SPONSORSHIP_ATTENDEES", chamberId, eventId: selectedEventId,
        entityId: editTarget.id,
        input: buildGuardedEventAttendeeUpdateInput({
          input, deletedAttendeeIds: [], expectedUpdatedAtUtcMilli: editTarget.updatedAtUtcMilli,
        }),
      }) : null;
      const result = await updateSponsorship({
        context: eventSalesCallerHandledApolloContext,
        variables: { input: prepared?.input || input },
      });
      const updateResult = result.data?.updateCmpEventSponsorshipRequest;
      if (
        !updateResult?.sponsorshipRequestSavedSuccessfully ||
        updateResult.sponsorshipRequest?.id !== editTarget.id ||
        updateResult.sponsorshipRequest.chamberId !== chamberId ||
        updateResult.sponsorshipRequest.eventId !== selectedEventId
      ) {
        throw new Error("The attendee update could not be confirmed.");
      }
      await refetchSponsorships();
      if (prepared) completeEventSalesOperation({ operation: prepared });
      setEditTarget(null);
      setSuccessMessage({
        message: eventSalesChangesSavedMessage,
        highlights: [],
      });
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error("Could not update attendee details.");
    }
  };

  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const eventName = selectedEvent?.title || "Event";

  const analyticsCards = [
    { label: "Event Sponsors", value: String(analytics.sponsors) },
    { label: "Sponsors Paid", value: String(analytics.paid) },
    { label: "Sponsors Unpaid", value: String(analytics.unpaid) },
    {
      label: "Event Sponsor Tickets Issued",
      value: String(analytics.ticketsAllocated),
    },
    {
      label: "Event Sponsorship Revenue",
      value: formatCurrencyFromCents(analytics.revenueInCents),
    },
  ];

  const currentCreateFormState = getSponsorshipInvoiceFormDirtyState({
    attendees,
    billing,
    dueDate,
    invoiceDate,
    packageId,
    referencePurchaseOrder,
    taxExempt,
  });
  const createFormDirty = hasUnsavedFormChanges({
    baseline: createFormBaseline,
    current: currentCreateFormState,
  });

  // The preview document line; Save Draft stores the same line.
  const sponsorshipDraftDocumentLines = [
    {
      code: selectedPackage?.code,
      description: getEventSponsorshipInvoiceLineDescription({
        eventDisplayName: getEventInvoiceDisplayName(selectedEvent),
        packageName: selectedPackage ? selectedPackage.name || "" : null,
      }),
      id: selectedPackage?.id || "sponsorship-package",
      quantity: 1,
      unitPriceInCents,
      totalInCents: lineSubtotalInCents,
      note: getSponsorshipComplimentaryTicketNote(includedQuantity),
    },
  ];

  // Design (15) "Save Draft" (Monday D30): the form becomes a Draft invoice
  // in Manage All Invoices; no sponsorship level is held yet.
  const handleSaveDraft = async () => {
    setPreviewOpen(false);
    await runEventSalesSingleFlight({
      inFlightGuard: createSponsorshipInFlightRef,
      execute: async () => {
        const errors = validateCreate();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
          scrollToFirstInvalidField();
          return;
        }
        const customerName = billing.customerName.trim() || "the member";
        try {
          const result = await runEventSalesOperation({
            action: "SAVE_INVOICE_DRAFT",
            chamberId,
            entityId: activeDraft?.id || "new-sponsorship-invoice-draft",
            eventId: selectedEventId,
            input: {
              chamberId,
              ...(activeDraft ? {
                invoiceId: activeDraft.id,
                expectedUpdatedAtUtcMilli: activeDraft.updatedAtUtcMilli,
              } : {}),
              sourceKind: CmpOperationalSourceKindEnum.EventSponsorship,
              eventId: selectedEventId,
              chamberMemberId: billing.selectedMemberCompanyId || undefined,
              billingSnapshot: {
                customerName: billing.customerName,
                contactName: billing.billingContactName || undefined,
                contactEmail: billing.billingEmail,
                contactPhone: billing.billingPhone || undefined,
                addressLine1: billing.streetAddress || undefined,
                addressLine2: billing.addressLine2 || undefined,
                city: billing.city || undefined,
                province: billing.province || undefined,
                postalCode: billing.postalCode || undefined,
                country: billing.country || undefined,
              },
              invoiceDetails: {
                dueDate,
                invoiceDate,
                referencePurchaseOrder:
                  normalizeEventInvoiceReference(referencePurchaseOrder) || undefined,
              },
              lineItems: toEventInvoiceDraftLineInputs(sponsorshipDraftDocumentLines.map((line) => ({
                ...line,
                description: line.description || "Sponsorship",
              }))),
              taxAmountInCents: lineTaxInCents,
              draftForm: serializeEventInvoiceDraftForm({
                eventId: selectedEventId,
                soldTo,
                billing,
                packageId,
                attendees,
                invoiceDate,
                dueDate,
                referencePurchaseOrder,
              }),
            },
            execute: (input) => saveEventInvoiceDraft({ variables: { input } }),
            isAuthoritativelyCompleted: (mutationResult) =>
              Boolean(mutationResult.data?.saveCmpEventInvoiceDraft.invoiceSavedSuccessfully),
          });
          const saved = result.data?.saveCmpEventInvoiceDraft.invoice;
          if (!saved) throw new Error("The draft could not be confirmed after saving.");
          setActiveDraft({ id: saved.id, updatedAtUtcMilli: saved.updatedAtUtcMilli || 0 });
          setCreateFormBaseline(currentCreateFormState);
          setDraftSavedCustomerName(customerName);
        } catch (error) {
          setErrorModal({
            title: "Draft not saved",
            message: getUserFacingErrorText(
              error,
              buildStaffCheckFirstFallback(
                "confirm that the draft was saved",
                "Refresh the page and check the draft",
              ),
            ),
          });
        }
      },
    });
  };

  const handleSelectedEventChange = (nextEventId: string) => {
    if (
      mode === "create" &&
      shouldConfirmEventSelectionChange({
        currentEventId: selectedEventId,
        formDirty: createFormDirty,
        nextEventId,
      })
    ) {
      setPendingSelectedEventId(nextEventId);
      return;
    }

    setSelectedEventId(nextEventId);
    clearFieldErrors("event");
  };

  const handleConfirmSelectedEventChange = () => {
    if (!pendingSelectedEventId) {
      return;
    }

    const nextEventId = pendingSelectedEventId;
    resetCreateForm();
    setSelectedEventId(nextEventId);
    setPendingSelectedEventId(null);
  };

  return (
    <InvoiceDesignScope>
      <FormPageShell
        headingId="sponsorship-sales-page"
        heading={pageTitle}
        mainClassName="w-full"
        mainPaddingTopClassName="pt-[18px]"
        contentClassName="space-y-[26px]"
        beforeMain={
          <>
            <UnsavedChangesPrompt
              variant="page"
              when={mode === "create" && createFormDirty}
              saving={creating || approving}
            />
            {pendingSelectedEventId ? (
              <UnsavedChangesDialog
                onLeave={handleConfirmSelectedEventChange}
                onStay={() => setPendingSelectedEventId(null)}
              />
            ) : null}
            <DesignDialog
              open={noSponsorshipEventTitle !== null}
              onClose={() => setNoSponsorshipEventTitle(null)}
              title="No Sponsorship Created"
              iconColor="#E1A731"
              iconNode={
                <svg aria-hidden="true" width="58%" height="58%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7.5v5.5M12 16.5v.01" />
                </svg>
              }
              message={
                <>
                  No sponsorship levels have been created for the{" "}
                  <strong>{noSponsorshipEventTitle}</strong>. Go to Manage
                  Events → Edit Event and add sponsorship levels before
                  creating a sponsorship invoice.
                </>
              }
              actions={
                <DesignDialogButton onClick={() => setNoSponsorshipEventTitle(null)}>
                  OK
                </DesignDialogButton>
              }
            />
          </>
        }
      >
        <EventSalesSection
          id="select-event"
          variant="plain"
          title="Select Event"
          required={mode === "create"}
          headerClassName="mb-[10px] flex flex-col gap-2"
          descriptionClassName="text-[14px] leading-[18px] text-black"
          titleClassName="text-[17px] font-semibold leading-[22px] text-black"
          description="Search or select the event from the drop down."
        >
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-between">
            <div className="w-full md:max-w-[600px] md:flex-1">
              <EventSelect
                inlineSearch={mode === "create"}
                error={Boolean(fieldErrors.event)}
                helperText={fieldErrors.event}
                options={events}
                value={selectedEventId}
                onChange={handleSelectedEventChange}
                loading={eventsLoading}
                placeholder={
                  mode === "manage" ? "Search Event" : "Select Event"
                }
              />
            </div>

          </div>
          {selectedEventPaymentRestriction ? (
            <Alert className="mt-4" severity="warning">
              {/* The reason after the first sentence is server text. */}
              You cannot record payments or change sales for this event right
              now. {selectedEventPaymentRestriction}
            </Alert>
          ) : null}
        </EventSalesSection>

        {mode === "create" ? (
          <EventSalesSection
            id="sold-to"
            variant="plain"
            title="Sold To"
            required
            headerClassName="mb-[10px] flex flex-col gap-2"
            descriptionClassName="text-[14px] leading-[18px] text-black"
            titleClassName="text-[17px] font-semibold leading-[22px] text-black"
            description="Select whether this invoice is for an existing member or a non-member."
          >
            <SoldToToggle value={soldTo} onChange={handleSoldToChange} />
          </EventSalesSection>
        ) : null}

        {mode === "manage" ? (
          <EventSalesSection
            id="sponsorship-analytics"
            variant="plain"
            title={`${eventName} Sponsorship Analytics`}
            description={`See a quick overview of your ${noun.singular} sponsorship details for this event`}
          >
            <EventStatCardRow cards={analyticsCards} hideIcons />
          </EventSalesSection>
        ) : null}

        {mode === "create" ? (
          <EventSalesSection
            id="create-sponsorship-invoice"
            showHeader={false}
            title="Create Sponsorship Invoice"
            variant="plain"
            description="Complete the information below, to create sponsorship invoice, and assign complimentary tickets if included in the sponsorship level."
          >
            <div className="space-y-[26px]">
              <div>
                <MemberBillingSelector
                  members={members}
                  billing={billing}
                  errors={fieldErrors}
                  onChange={handleBillingChange}
                  onClearError={clearFieldErrors}
                  loading={lookupsLoading}
                  mode={soldTo}
                  onSearchChange={setMemberSearch}
                  heading="Select Company to Invoice"
                  headingClassName="text-[17px] font-bold leading-[22px] text-gray-950"
                  helperText="Edit contact, email and phone number, if different than what auto-populates."
                  memberLabelClassName="text-[15px] font-bold leading-[20px] text-gray-950"
                  billingFieldLabelClassName="text-[15px] font-bold leading-[20px] text-gray-950"
                  billingAddressHelperText={
                    soldTo === "non-member"
                      ? "Enter the billing/mailing address for this customer."
                      : "Primary address on file will be selected if billing/mailing address has not been provided. Edit details as required."
                  }
                />
              </div>

              <EventInvoiceCreateFields
                dueDate={dueDate}
                dueDateError={fieldErrors.dueDate}
                headingClassName="text-[17px] font-bold leading-[22px] text-gray-950"
                invoiceDate={invoiceDate}
                invoiceDateError={fieldErrors.invoiceDate}
                onDueDateChange={(value) => {
                  setDueDate(value);
                  clearFieldErrors("dueDate");
                }}
                onInvoiceDateChange={(value) => {
                  setInvoiceDate(value);
                  setDueDate(
                    getEventInvoiceDueDateForIssueDate({
                      invoiceDate: value,
                      invoicePaymentTerms,
                      timeZone: chamberTimeZone,
                    }),
                  );
                  clearFieldErrors("invoiceDate", "dueDate");
                }}
                onReferenceChange={setReferencePurchaseOrder}
                reference={referencePurchaseOrder}
              />

              <div className="space-y-[26px]">
                <h3 className="text-[17px] font-bold leading-[22px] text-gray-950">
                  Enter Sponsorship Description to Create Invoice
                </h3>
                <div className="overflow-x-auto">
                  <div className="min-w-[680px]">
                    <div className="grid grid-cols-[minmax(100px,.6fr)_minmax(260px,2.2fr)_minmax(160px,1fr)] rounded-t bg-[#0B9DF2]">
                      {["Code", "Description", "Sponsorship Price"].map(
                        (heading) => (
                          <div
                            className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-white"
                            key={heading}
                          >
                            {heading}
                          </div>
                        ),
                      )}
                    </div>
                    <div className="grid grid-cols-[minmax(100px,.6fr)_minmax(260px,2.2fr)_minmax(160px,1fr)] items-start gap-4 py-3">
                      <TextField
                        disabled
                        placeholder="Code"
                        size="small"
                        sx={disabledFieldSx}
                        value={selectedPackage?.code || ""}
                      />
                      <InvoiceSearchableSelect
                        id="sponsorship-level"
                        getOptionDisabled={(option) => {
                          const remaining = packageRemaining(option);

                          return (
                            remaining != null &&
                            remaining <= 0 &&
                            option.id !== packageId
                          );
                        }}
                        getOptionLabel={(option) => {
                          const remaining = packageRemaining(option);

                          return remaining != null && remaining <= 0
                            ? `${option.name} (Sold Out)`
                            : option.name;
                        }}
                        getOptionKey={(option) => option.id}
                        onChange={(option) =>
                          handleSelectPackage(option?.id || "")
                        }
                        options={sponsorshipPackages}
                        value={selectedPackage || null}
                        disabled={!eventDetail}
                        error={Boolean(fieldErrors.sponsorshipLevel)}
                        helperText={fieldErrors.sponsorshipLevel}
                        placeholder={
                          !eventDetail
                            ? "Select an event first"
                            : "Select Sponsorship Level"
                        }
                        searchPlaceholder="Search Level"
                      />
                      <TextField
                        disabled
                        placeholder="$0.00"
                        size="small"
                        sx={disabledFieldSx}
                        value={
                          selectedPackage
                            ? formatCurrencyFromCents(unitPriceInCents)
                            : ""
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {attendees.length > 0 && selectedPackage ? (
                // Design D:1823552: "{Level} Sponsorship Includes N Tickets"
                // with one card per included attendee.
                <EventInvoiceAttendeeFields
                  attendees={attendees}
                  choiceFields={attendeeChoiceFields}
                  disabled={pendingCreate || creating || approving}
                  errorKeyPrefix="attendees"
                  errors={fieldErrors}
                  helperText="Tickets included with the above sponsorship. Add attendee names and emails and tickets will be emailed once the sponsorship is paid."
                  onChange={handleAttendeesChange}
                  onClearError={clearFieldErrors}
                  primaryAttendeeId={getEventInvoicePrimaryAttendeeId([attendees])}
                  suggestions={attendeeSuggestions}
                  companyOptions={attendeeCompanyOptions}
                  title={getSponsorshipIncludedTicketsHeading(
                    selectedPackage.name,
                    includedQuantity,
                  )}
                />
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-5 pt-1">
                <PreviewInvoiceButton onClick={handleOpenDraftPreview} />
                <Button
                  variant="contained"
                  disabled={creating || approving}
                  onClick={() => void handleInvoiceSponsorship()}
                  startIcon={
                    creating || approving ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }
                  sx={createInvoiceButtonSx}
                >
                  Create Invoice
                </Button>
              </div>
            </div>
          </EventSalesSection>
        ) : null}

        {mode === "manage" ? (
          <>
            <EventSalesSection
              id="sponsorship-level-filters"
              variant="plain"
              title="Sponsorship Level Filters"
              description="Use filters to easily sort information"
            >
              <EventSalesFilterBar
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search by Company Name"
                searchMinWidth={260}
                filters={[
                  {
                    label: "Filter by Invoice Status",
                    active: invoiceStatusFilters.length > 0,
                    onOpen: setInvoiceStatusMenuAnchor,
                    selectedLabels: invoiceStatusFilters.map(
                      (value) =>
                        eventInvoiceSelectableStatusFilterOptions.find(
                          (option) => option.value === value,
                        )?.label || value,
                    ),
                    width: 240,
                  },
                  {
                    label: "Filter by Sponsorship Type",
                    active: packageFilters.length > 0,
                    onOpen: setPackageMenuAnchor,
                    selectedLabels: packageFilters.map(
                      (value) =>
                        sponsorshipPackages.find((pkg) => pkg.id === value)
                          ?.name || value,
                    ),
                    width: 275,
                  },
                ]}
                onClearAllFilters={() => {
                  clearSponsorshipFilters();
                }}
              />

              <EventSalesSearchableMultiSelectMenu
                anchorEl={invoiceStatusMenuAnchor}
                ariaLabel="Invoice status filters"
                onChange={setInvoiceStatusFilters}
                onClose={() => setInvoiceStatusMenuAnchor(null)}
                options={eventInvoiceSelectableStatusFilterOptions}
                searchPlaceholder="Search Invoice Status"
                values={invoiceStatusFilters}
              />
              <EventSalesSearchableMultiSelectMenu
                anchorEl={packageMenuAnchor}
                ariaLabel="Sponsorship type filters"
                onChange={setPackageFilters}
                onClose={() => setPackageMenuAnchor(null)}
                options={sponsorshipPackages.map((pkg) => ({
                  label: pkg.name,
                  value: pkg.id,
                }))}
                searchPlaceholder="Search Sponsorship Type"
                values={packageFilters}
              />
            </EventSalesSection>

            <EventSalesSection
              id="manage-sponsors-list"
              action={<Button
                variant="contained"
                startIcon={<FileUploadOutlinedIcon />}
                disabled={exportingSponsors || !selectedEventId}
                onClick={handleExportSponsors}
                sx={goldButtonSx}
              >
                Export Sponsor List
              </Button>}
              variant="plain"
              title="Manage Sponsors List"
              description="View paid invoices and open invoices awaiting payment, and apply payment. Sponsorships awaiting payment will NOT have tickets emailed to attendees until the invoice is paid. Tickets will be issued and emailed to the following attendees once the Sponsorship has been paid. Important to note: once a ticket is issued, it can be updated internally, but a new ticket will not be issued."
            >
              <TableContainer className="rounded-[10px] border border-[#ECECEC]">
                <Table
                  size="small"
                  sx={{
                    minWidth: 1622,
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      borderColor: "#ECECEC",
                      fontSize: 14,
                      px: 2,
                      py: 1.75,
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "normal",
                      lineHeight: "18px",
                      py: 1.625,
                    },
                  }}
                >
                  <colgroup>
                    {[110, 308.6, 261.2, 237.4, 237.4, 237.4, 210].map(
                      (width, index) => (
                        <col key={`${width}-${index}`} style={{ width }} />
                      ),
                    )}
                  </colgroup>
                  <TableHead className="bg-[#F7F7F8]">
                    <TableRow>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Invoice #", "invoiceNumber")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Company Name", "companyName")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader(
                          "Sponsorship Type",
                          "sponsorshipType",
                        )}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Ticket Included", "tickets")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader(
                          "Invoice Status",
                          "invoiceStatus",
                        )}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Ticket Status", "ticketStatus")}
                      </TableCell>
                      {/* Actions is not sortable (mock). */}
                      <TableCell
                        className="!font-bold"
                        align="center"
                        sx={{
                          ...stickyActionColumnHeaderSx,
                          minWidth: 230,
                          width: 230,
                          ...salesActionCellButtonsSx,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sponsorshipsLoading && sponsorships.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" className="!py-8">
                          <CircularProgress size={22} sx={{ color: cpaGold }} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!sponsorshipsLoading && sortedSponsorships.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: "0 !important" }}>
                          <TableEmptyState
                            heading="No sponsorships match your filters"
                            onClear={clearSponsorshipFilters}
                            clearLabel="Clear All Filter"
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {sortedSponsorships.map((sponsorship) => {
                      const paidInvoice =
                        sponsorship.invoice?.invoiceStatus ===
                        CmpInvoiceStatusEnum.Paid;
                      const companyName =
                        sponsorship.selectedMemberCompanyNameSnapshot ||
                        sponsorship.sponsorDisplayName;
                      return (
                        <TableRow
                          data-chamber-id={sponsorship.chamberId}
                          data-event-id={sponsorship.eventId}
                          data-is-cmp-event-module-test-data={sponsorship.isCmpEventModuleTestData === true}
                          data-record-id={sponsorship.id}
                          key={sponsorship.id}
                        >
                          <TableCell>
                            {sponsorship.invoice ? (
                              <button
                                type="button"
                                className="font-semibold text-gray-950 underline"
                                onClick={() => openInvoice(sponsorship)}
                              >
                                {sponsorship.invoice.invoiceNumber}
                              </button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>{companyName}</TableCell>
                          <TableCell>
                            {sponsorship.sponsorshipPackageNameSnapshot}
                          </TableCell>
                          {/* D:7296 (EV15-84 / EV-165): normal weight, left aligned. */}
                          <TableCell>
                            {getEventSponsorshipAttendeeCount(sponsorship)}
                          </TableCell>
                          <TableCell>
                            {sponsorshipInvoiceStatusLabel(
                              sponsorship,
                              chamberTimeZone,
                            )}
                          </TableCell>
                          <TableCell>
                            {sponsorTicketStatusLabel(sponsorship)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...stickyActionColumnCellSx,
                              minWidth: 230,
                              width: 230,
                              ...salesActionCellButtonsSx,
                              // D:7300-7301: 11px buttons on Manage Sponsorship Sales.
                              "& .MuiButton-root": { ...salesActionCellButtonsSx["& .MuiButton-root"], fontSize: 11 },
                            }}
                          >
                            <div className="flex flex-col gap-[6px]">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => openEditAttendees(sponsorship)}
                                sx={goldButtonSx}
                              >
                                Update Attendee Details
                              </Button>
                              {sponsorship.invoice ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => openInvoice(sponsorship)}
                                  sx={blackButtonSx}
                                >
                                  {paidInvoice
                                    ? "View Invoice"
                                    : "Open Invoice"}
                                </Button>
                              ) : routeTarget?.sourceId === sponsorship.id ? (
                                <span className="text-xs font-semibold text-amber-700">
                                  Invoice {routeTarget.invoiceNumber} requires
                                  support reconciliation.
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </EventSalesSection>

            {/* Process payment (Open Invoice) */}
            <ManageInvoiceActionConfirmationDialog
              customerName={
                paymentConfirmationTarget?.billingSnapshot?.customerName ||
                paymentConfirmationTarget?.selectedMemberCompanyNameSnapshot ||
                paymentConfirmationTarget?.sponsorDisplayName ||
                "Customer"
              }
              kind="PROCESS_PAYMENT"
              onClose={() => setPaymentConfirmationTarget(null)}
              onConfirm={() => {
                const sponsorship = paymentConfirmationTarget;

                if (
                  !sponsorship?.invoice ||
                  sponsorship.invoice.amountDueInCents <= 0 ||
                  sponsorship.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                ) {
                  setPaymentConfirmationTarget(null);
                  setErrorModal({
                    title: "Payment unavailable",
                    message:
                      sponsorship?.paymentActionUnavailableReason ||
                      selectedEventPaymentRestriction ||
                      (sponsorship?.invoice
                        ? EVENT_INVOICE_NOTHING_TO_PAY_TEXT
                        : EVENT_INVOICE_NOT_FOUND_TEXT),
                  });
                  return;
                }

                setPaymentConfirmationTarget(null);
                setPaymentTarget(sponsorship);
              }}
              open={Boolean(paymentConfirmationTarget)}
            />

            <ProcessPaymentModal
              open={Boolean(paymentTarget)}
              customerName={
                currentPaymentTarget
                  ? currentPaymentTarget.selectedMemberCompanyNameSnapshot ||
                    currentPaymentTarget.sponsorDisplayName
                  : ""
              }
              invoiceNumber={currentPaymentTarget?.invoice?.invoiceNumber || ""}
              amountDueInCents={
                currentPaymentTarget?.invoice?.amountDueInCents || 0
              }
              invoiceLineItems={currentPaymentTarget?.invoice?.lineItems || []}
              subtotalAmountInCents={
                currentPaymentTarget?.invoice?.subtotalAmountInCents
              }
              taxAmountInCents={currentPaymentTarget?.invoice?.taxAmountInCents}
              totalAmountInCents={
                currentPaymentTarget?.invoice?.totalAmountInCents
              }
              paymentMethods={currentManualPaymentMethods}
              creditCardAvailable={Boolean(
                currentPaymentRules?.creditCardAllowed &&
                !currentPaymentUnavailableReason,
              )}
              paymentActionUnavailableReason={currentPaymentUnavailableReason}
              requireManualPaymentConfirmation={false}
              loading={recordingPayment}
              onSubmit={handleRecordPayment}
              onClose={() => setPaymentTarget(null)}
              renderSecureCardCheckout={({
          amountInCents,
          beforeSubmit,
          cardNote,
          onPaymentInProgressChange,
        }) =>
                currentPaymentTarget?.invoice ? (
                  <EventSecureCardCheckoutPanel
                    onPaymentInProgressChange={onPaymentInProgressChange}
                    key={`sponsorship-checkout:${currentPaymentTarget.id}:${currentPaymentTarget.invoice.id}:${amountInCents}`}
                    amountDueInCents={amountInCents}
                    beforeSubmit={beforeSubmit}
                    cardNote={cardNote}
                    chamberId={chamberId}
                    eventId={selectedEventId}
                    invoiceId={currentPaymentTarget.invoice.id}
                    invoiceStatus={currentPaymentTarget.invoice.invoiceStatus}
                    onPaymentProcessed={async () => {
                      try {
                        const readback = await refetchSponsorships();
                        const savedSponsorship =
                          readback.data?.getCmpEventSponsorshipRequests.sponsorshipRequests.find(
                            (sponsorship) =>
                              sponsorship.id === currentPaymentTarget.id,
                          );
                        const successDetails = getEventPaymentSuccessDetails(
                          savedSponsorship,
                          currentPaymentTarget.billingSnapshot?.customerName ||
                            currentPaymentTarget.sponsorDisplayName ||
                            currentPaymentTarget.selectedMemberCompanyNameSnapshot ||
                            "Customer",
                        );

                        if (!successDetails) {
                          throw new Error(
                            "The saved sponsorship payment receipt could not be confirmed.",
                          );
                        }

                        setPaymentTarget(null);
                        setPaymentSuccess(successDetails);
                      } catch (error) {
                        // The card payment was processed; only the
                        // receipt read after it did not finish.
                        setErrorModal({
                          title: "Card payment processed",
                          message: eventCardPaymentReceiptUnavailableMessage,
                        });
                      }
                    }}
                    onRefreshAuthoritativeStatus={async () => {
                      const readback = await refetchSponsorships();
                      return getEventSecureCardCheckoutReadback(
                        readback.data?.getCmpEventSponsorshipRequests.sponsorshipRequests.find(source => source.id === currentPaymentTarget.id),
                        currentPaymentTarget.invoice!.id,
                      );
                    }}
                    paymentStatus={currentPaymentTarget.paymentStatus}
                    purpose="SPONSORSHIP"
                    sourceId={currentPaymentTarget.id}
                    sourceStatus={currentPaymentTarget.status}
                  />
                ) : null
              }
            />

            {currentDocumentTarget?.invoice ? (
              <SavedEventInvoiceDocumentModal
                actionLoading={downloadingPdf || emailingInvoiceDocument}
                chamber={chamber}
                chamberId={chamberId}
                customerName={
                  currentDocumentTarget.selectedMemberCompanyNameSnapshot ||
                  currentDocumentTarget.sponsorDisplayName
                }
                documentMode={
                  documentModeOverride ||
                  getEventInvoiceDocumentMode(
                    currentDocumentTarget.invoice.invoiceStatus,
                  ) ||
                  "INVOICE"
                }
                invoiceId={currentDocumentTarget.invoice.id}
                invoiceKind={CmpInvoiceKindEnum.EventSponsorship}
                invoiceNumber={currentDocumentTarget.invoice.invoiceNumber}
                recipientEmail={
                  currentDocumentTarget.billingSnapshot?.contactEmail ||
                  currentDocumentTarget.purchaserEmail
                }
                sourceEmailPermission={canEmailSavedEventDocuments}
                onAddPayment={
                  currentDocumentTarget.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                    ? undefined
                    : () => {
                        const sponsorship = currentDocumentTarget;

                        setDocumentTarget(null);
                        setDocumentModeOverride(null);
                        setPaymentConfirmationTarget(sponsorship);
                      }
                }
                onClose={() => {
                  setDocumentTarget(null);
                  setDocumentModeOverride(null);
                }}
                onPrintPdf={() =>
                  downloadInvoicePdf(currentDocumentTarget.invoice?.id)
                }
                paymentActionUnavailableReason={
                  currentDocumentTarget.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                }
                onEmail={(documentMode) => {
                  const invoiceId =
                    currentDocumentTarget.invoice?.id.trim() || "";

                  if (!invoiceId) {
                    setErrorModal({
                      title: "Invoice not emailed",
                      message:
                        "We could not find the saved sponsorship invoice. Refresh the page and try again.",
                    });
                    return;
                  }

                  const customerName =
                    currentDocumentTarget.billingSnapshot?.customerName ||
                    currentDocumentTarget.selectedMemberCompanyNameSnapshot ||
                    currentDocumentTarget.sponsorDisplayName ||
                    "Customer";

                  setPendingDocumentEmail({
                    customerName,
                    invoiceId,
                    invoiceLabel: `${currentDocumentTarget.invoice?.invoiceNumber || "Sponsorship invoice"} email`,
                    mode:
                      documentMode === "PAYMENT_RECEIPT"
                        ? CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
                        : CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
                    paymentId:
                      documentMode === "PAYMENT_RECEIPT"
                        ? currentDocumentTarget.paymentId
                        : undefined,
                    recipientEmail:
                      currentDocumentTarget.billingSnapshot?.contactEmail ||
                      currentDocumentTarget.purchaserEmail,
                  });
                }}
                open={Boolean(documentTarget)}
              />
            ) : null}

            {/* Update attendee details (shared modal, "numbered" variant
          preserves this page's existing DOM) */}
            <UpdateAttendeeNamesModal
              companyOptions={attendeeCompanyOptions}
              nameOptions={editAttendeeSuggestions}
              choiceFieldsForAttendee={(attendee) =>
                getEventAttendeeChoiceFields({
                  customFields: eventDetail?.customFields || [],
                  ticketTypeId: attendee.ticketTypeId,
                })
              }
              open={Boolean(editTarget)}
              title="Update Attendee Details"
              description={getEventAttendeeUpdateHelperText({
                awaitingPayment: Boolean(
                  editTarget &&
                    !isPaidEventPaymentStatus(editTarget.paymentStatus),
                ),
                kind: "sponsorship",
              })}
              attendees={editAttendees.filter(({ id }) =>
                editTarget && [...editTarget.includedAttendees, ...(editTarget.additionalAttendees || [])].some((attendee) => attendee.id === id && attendee.status !== CmpEventRegistrationStatusEnum.Canceled),
              )}
              onAttendeeChange={(id, patch) =>
                setEditAttendees((current) =>
                  current.map((item) =>
                    item.id === id ? { ...item, ...patch } : item,
                  ),
                )
              }
              onClose={() => setEditTarget(null)}
              onSave={handleSaveAttendeeNames}
              saving={updatingSponsorship}
              variant="numbered"
              // Claude Design (13) ds-components/UpdateAttendeeNamesModal.jsx:130, 230.
              emptyMessage="No attendees assigned. Save to leave this complimentary ticket unassigned."
              attendeeTicketLabel="complimentary ticket"
              additionalComments={editTarget?.additionalComments}
            />
          </>
        ) : null}

        <EventInvoiceDraftPreview
          members={members}
          open={mode === "create" && previewOpen}
          billing={billing}
          chamber={chamber}
          creating={creating || approving || savingDraft}
          onCreate={() => {
            setPreviewOpen(false);
            void handleInvoiceSponsorship();
          }}
          onSaveDraft={handleSaveDraft}
          onClose={() => setPreviewOpen(false)}
          invoiceKind={CmpInvoiceKindEnum.EventSponsorship}
          eventCode={selectedEvent?.eventCode}
          eventName={eventName}
          eventStartAtUtcMilli={selectedEvent?.startAtUtcMilli}
          eventTimeZone={eventDetail?.timeZone || chamberTimeZone}
          dueDate={dueDate}
          invoiceDate={invoiceDate}
          manualPaymentMethods={draftPaymentMethods}
          lineItems={sponsorshipDraftDocumentLines}
          taxNote={getSponsorshipDraftTaxNote({ mode: eventDetail?.sponsorshipTaxMode, taxInCents: lineTaxInCents })}
          referencePurchaseOrder={normalizeEventInvoiceReference(
            referencePurchaseOrder,
          )}
          subtotalInCents={lineSubtotalInCents}
          taxInCents={lineTaxInCents}
          taxLabel={`${taxRateLabel} (${taxRatePercentage}%)`}
          taxRegistrationNumber={
            settingsData?.getChamberMemberProSettings.paymentsAndTaxes
              .taxRegistrationNumber
          }
          totalInCents={lineTotalInCents}
        />

        <ActionSuccessDialog
          message={successMessage?.message}
          highlights={successMessage?.highlights}
          onClose={() => setSuccessMessage(null)}
        />

        <ActionSuccessDialog
          highlights={draftSavedCustomerName ? [draftSavedCustomerName] : undefined}
          message={draftSavedCustomerName ? getEventInvoiceDraftSavedMessage(draftSavedCustomerName) : null}
          onClose={() => {
            setDraftSavedCustomerName(null);
            navigate(RoutePaths.chamberAdmin_invoicing_manage);
          }}
        />

        <PaymentProcessedDialog
          actionLoading={emailingInvoiceDocument}
          billingContactEmail={paymentSuccess?.billingContactEmail}
          customerName={paymentSuccess?.customerName || "Customer"}
          onClose={() => setPaymentSuccess(null)}
          onPrintReceipt={
            paymentSuccess &&
            sponsorships.some(
              (sponsorship) =>
                sponsorship.invoice?.id === paymentSuccess.invoiceId &&
                sponsorship.paymentId === paymentSuccess.paymentId,
            )
              ? () => {
                  const savedSponsorship = sponsorships.find(
                    (sponsorship) =>
                      sponsorship.invoice?.id === paymentSuccess.invoiceId &&
                      sponsorship.paymentId === paymentSuccess.paymentId,
                  );

                  if (savedSponsorship) {
                    setPaymentSuccess(null);
                    setDocumentModeOverride("PAYMENT_RECEIPT");
                    setDocumentTarget(savedSponsorship);
                  }
                }
              : undefined
          }
          onEmailReceipt={
            paymentSuccess && canEmailPaymentSuccessReceipt
              ? () => {
                  const receipt = paymentSuccess;

                  setPaymentSuccess(null);
                  setPendingDocumentEmail({
                    customerName: receipt.customerName,
                    invoiceId: receipt.invoiceId,
                    invoiceLabel: "Sponsorship payment receipt email",
                    mode: CmpInvoiceDocumentEmailModeEnum.PaymentReceipt,
                    paymentId: receipt.paymentId,
                    recipientEmail: receipt.billingContactEmail,
                  });
                }
              : undefined
          }
          open={Boolean(paymentSuccess)}
        />

        <ManageInvoiceActionConfirmationDialog
          customerName={pendingDocumentEmail?.customerName || "Customer"}
          kind={
            pendingDocumentEmail?.mode ===
            CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
              ? "EMAIL_RECEIPT"
              : "EMAIL_INVOICE"
          }
          loading={emailingInvoiceDocument}
          onClose={() => setPendingDocumentEmail(null)}
          onConfirm={async () => {
            const pendingEmail = pendingDocumentEmail;

            if (!pendingEmail) {
              return;
            }

            const emailResult = await emailSavedEventInvoiceDocument({
              chamberId,
              execute: (input) =>
                emailCmpInvoiceDocument({ variables: { input } }),
              invoiceId: pendingEmail.invoiceId,
              invoiceLabel: pendingEmail.invoiceLabel,
              mode: pendingEmail.mode,
              paymentId: pendingEmail.paymentId,
            });
            setEmailSuccessMessage(
              pendingEmail.mode ===
                CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
                ? `Receipt emailed successfully to ${emailResult.toEmail} for ${pendingEmail.customerName}!`
                : `Email sent successfully to ${emailResult.toEmail} for ${pendingEmail.customerName}!`,
            );
            setPendingDocumentEmail(null);
          }}
          open={Boolean(pendingDocumentEmail)}
          recipientEmail={pendingDocumentEmail?.recipientEmail}
        />

        <InvoiceSuccessDialog
          message={emailSuccessMessage || ""}
          onClose={() => setEmailSuccessMessage(null)}
          open={Boolean(emailSuccessMessage)}
        />

        <InvoiceCreatedDialog
          actionError={createdInvoiceActionError}
          message="Sponsorship Invoice has been generated, would you like to print or email the invoice?"
          onClose={() => {
            setCreatedInvoice(null);
            setCreatedInvoiceActionError(null);
          }}
          emailing={emailingInvoiceDocument}
          onEmail={
            createdInvoice
              ? async () => {
                  try {
                    setCreatedInvoiceActionError(null);
                    const emailResult = await emailSavedEventInvoiceDocument({
                      chamberId,
                      execute: (input) =>
                        emailCmpInvoiceDocument({ variables: { input } }),
                      invoiceId: createdInvoice.invoiceId,
                      invoiceLabel: "Sponsorship invoice email",
                      mode: CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
                    });

                    return {
                      actualRecipientEmail: emailResult.toEmail,
                      successMessage: emailResult.successMessage,
                    };
                  } catch (error) {
                    setCreatedInvoiceActionError(
                      getUserFacingErrorText(
                        error,
                        buildStaffCheckFirstFallback(
                          "confirm that the sponsorship invoice was emailed",
                          "Refresh the page and check the invoice",
                        ),
                      ),
                    );
                    throw error;
                  }
                }
              : undefined
          }
          onPrint={async () => {
            if (!createdInvoice) {
              return;
            }

            try {
              setCreatedInvoiceActionError(null);
              await downloadInvoicePdf(createdInvoice.invoiceId);
            } catch (error) {
              setCreatedInvoiceActionError(
                getUserFacingErrorText(
                  error,
                  buildStaffErrorFallback("make the sponsorship invoice PDF"),
                ),
              );
            }
          }}
          open={Boolean(createdInvoice)}
          printing={downloadingPdf}
          recipientEmail={createdInvoice?.recipientEmail}
          title="Sponsorship Invoice Created"
        />

        <ConfirmDialog
          open={Boolean(sponsorshipToFinish)}
          tone="warning"
          title="Finish the sponsorship invoice?"
          message={
            sponsorshipToFinish
              ? `${sponsorshipToFinish.sponsorship.sponsorDisplayName} (${sponsorshipToFinish.sponsorship.sponsorshipPackageNameSnapshot}) is saved, but its invoice was not created. Finish the invoice to complete this sponsorship. No second sponsorship is created.`
              : undefined
          }
          confirmLabel="Finish invoice"
          cancelLabel="Not now"
          errorMessage={sponsorshipToFinish?.error}
          loading={approving}
          onConfirm={handleFinishSponsorshipInvoice}
          onCancel={() => setSponsorshipToFinish(null)}
        />

        {/* API errors surface here as a popup, never a header toast/Alert. */}
        <ResultModal
          open={Boolean(errorModal)}
          success={false}
          title={errorModal?.title || ""}
          message={errorModal?.message}
          onClose={() => setErrorModal(null)}
        />
      </FormPageShell>
    </InvoiceDesignScope>
  );
};

type InvoiceManageSponsorshipSalesProps = Readonly<{
  mode: EventSalesPageMode;
}>;

const InvoiceManageSponsorshipSales = ({
  mode,
}: InvoiceManageSponsorshipSalesProps): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "sponsorship-sales",
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return (
    <InvoiceManageSponsorshipSalesContent
      attendeeEditTarget={getEventSalesAttendeeEditTarget(location.search)}
      mode={mode}
      routeTarget={getCmpEventSalesRouteTarget(location.search)}
    />
  );
};

export default InvoiceManageSponsorshipSales;
